import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  LabelList,
} from "recharts";

import CheckCircleIcon from "@material-ui/icons/CheckCircle";

const getBarColor = (count) => {
  if (count > 100) return "#D32F2F";
  if (count >= 90) return "#F57C00";
  if (count >= 80) return "#FB8C00";
  if (count <= 70) return "#FFA000";
  return "#FFF8E1";
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    return (
      <div
        style={{
          background: "#fff",
          borderRadius: 10,
          padding: "10px 14px",
          boxShadow: "0 6px 20px rgba(0,0,0,.15)",
          border: "none",
        }}
      >
        <div style={{ fontWeight: 600 }}>{payload[0].payload.name}</div>
        <div style={{ color: "#6366F1", marginTop: 4 }}>
          Activities: {payload[0].value}
        </div>
      </div>
    );
  }
  return null;
};

const CustomBarChart = ({ data = [], height, isPopup = false }) => {
  const hasData = data.length > 0;
  const chartHeight = height ?? (isPopup ? 600 : 320);

  const emptyPlaceholder = [
    { name: "", count: 0 },
    { name: " ", count: 0 },
    { name: "  ", count: 0 },
  ];

  return (
    <div style={{ position: "relative", width: "100%", height: chartHeight, marginTop: isPopup ? "45px" : "0px" }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={hasData ? data : emptyPlaceholder}
          margin={{ top: 15, right: 15, left: 5, bottom: hasData ? 15 : 28 }}
        >
          <XAxis
            dataKey="name"
            interval={0}
            angle={hasData ? -35 : 0}
            textAnchor={hasData ? "end" : "middle"}
            height={hasData ? 70 : 30}
            tickFormatter={(name) =>
              hasData && name?.length > 18
                ? `${name.substring(0, 18)}...`
                : name
            }
            tick={{ fontSize: isPopup ? 14 : 11, fill: "#4B5563" }}
            axisLine={{ stroke: "#D1D5DB" }}
            tickLine={{ stroke: "#D1D5DB" }}
          />

          <YAxis
            allowDecimals={false}
            axisLine={{ stroke: "#D1D5DB" }}
            tickLine={{ stroke: "#D1D5DB" }}
            tick={{ fontSize: isPopup ? 14 : 11, fill: "#4B5563" }}
            domain={hasData ? undefined : [0, 5]}
            ticks={hasData ? undefined : [0, 1, 2, 3, 4, 5]}
            width={hasData ? 60 : 40}
          />

          <Tooltip content={<CustomTooltip />} />

          {hasData && (
            <Bar
              dataKey="count"
              radius={[10, 10, 0, 0]}
              maxBarSize={isPopup ? 80 : 55}
            >
              <LabelList
                dataKey="count"
                position="top"
                style={{
                  fontSize: isPopup ? 14 : 11,
                  fontWeight: 600,
                }}
              />
              {data.map((item, index) => (
                <Cell key={index} fill={getBarColor(item.count)} />
              ))}
            </Bar>
          )}
        </BarChart>
      </ResponsiveContainer>

      {!hasData && (
        <>
          {/* "Count" label, rotated, vertically centered on the left */}
          <div
            style={{
              position: "absolute",
              left: -6,
              top: "50%",
              transform: "translateY(-50%) rotate(-90deg)",
              fontSize: 13,
              fontWeight: 500,
              color: "#6B7280",
              pointerEvents: "none",
              whiteSpace: "nowrap",
            }}
          >
            Count
          </div>

          {/* "Filenames" label, directly under the x-axis line */}
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 30,
              textAlign: "center",
              fontSize: 13,
              fontWeight: 500,
              color: "#6B7280",
              pointerEvents: "none",
            }}
          >
            Filenames
          </div>

          {/* Green checkmark, centered over the plot area */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              paddingBottom: 28,
              pointerEvents: "none",
            }}
          >
            <CheckCircleIcon style={{ fontSize: 60, color: "#4CAF50" }} />
          </div>
        </>
      )}
    </div>
  );
};

export default CustomBarChart;